(() => {
  const ELEMENT_NAME = 'age-verification';

  // The Liquid markup is hidden by default. This script reveals it only
  // after confirming that the visitor has not already been verified.

  if (customElements.get(ELEMENT_NAME)) {
    return;
  }

  class AgeVerification extends HTMLElement {
    constructor() {
      super();

      this.closeTimer = null;
      this.previousActiveElement = null;

      this.handleConfirm = this.handleConfirm.bind(this);
      this.handleDecline = this.handleDecline.bind(this);
      this.handleEditorClose = this.handleEditorClose.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleSectionSelect = this.handleSectionSelect.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.dataset.initialized = 'true';

      this.dialog = this.querySelector('.age-verification__dialog');
      this.content = this.querySelector('[data-age-gate-content]');
      this.deniedPanel = this.querySelector('[data-age-gate-denied]');
      this.deniedHeading = this.querySelector(
        '[data-age-gate-denied-heading]'
      );
      this.deniedDescription = this.querySelector(
        '[data-age-gate-denied-description]'
      );

      this.confirmButton = this.querySelector('[data-age-gate-confirm]');
      this.declineButton = this.querySelector('[data-age-gate-decline]');
      this.editorCloseButton = this.querySelector(
        '[data-age-gate-editor-close]'
      );

      this.defaultLabelledBy =
        this.dialog?.getAttribute('aria-labelledby') || '';

      this.defaultDescribedBy =
        this.dialog?.getAttribute('aria-describedby') || '';

      this.deniedLabelledBy = this.deniedHeading?.id || '';
      this.deniedDescribedBy = this.deniedDescription?.id || '';

      this.enabled = this.dataset.enabled === 'true';
      this.lockScroll = this.dataset.lockScroll === 'true';
      this.showInEditor = this.dataset.showInEditor === 'true';

      this.storageMode = this.dataset.storageMode || 'days';
      this.storageKey =
        this.dataset.storageKey || 'shopify-age-verification';

      this.rememberDays = this.clampNumber(
        Number.parseInt(this.dataset.rememberDays, 10),
        1,
        90,
        30
      );

      this.isDesignMode =
        Boolean(window.Shopify && window.Shopify.designMode) ||
        this.dataset.designMode === 'true';

      this.applyThemeSettings();
      this.showDefaultState();

      this.confirmButton?.addEventListener('click', this.handleConfirm);
      this.declineButton?.addEventListener('click', this.handleDecline);
      this.editorCloseButton?.addEventListener(
        'click',
        this.handleEditorClose
      );

      document.addEventListener(
        'shopify:section:select',
        this.handleSectionSelect
      );

      if (!this.enabled) {
        this.hideImmediately();
        return;
      }

      if (this.isDesignMode) {
        if (this.showInEditor) {
          this.open();
        } else {
          this.hideImmediately();
        }

        return;
      }

      if (this.hasValidConfirmation()) {
        this.hideImmediately();
        return;
      }

      this.open();
    }

    disconnectedCallback() {
      this.confirmButton?.removeEventListener(
        'click',
        this.handleConfirm
      );

      this.declineButton?.removeEventListener(
        'click',
        this.handleDecline
      );

      this.editorCloseButton?.removeEventListener(
        'click',
        this.handleEditorClose
      );

      document.removeEventListener('keydown', this.handleKeydown);

      document.removeEventListener(
        'shopify:section:select',
        this.handleSectionSelect
      );

      this.unlockPage();

      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
      }
    }

    applyThemeSettings() {
      const styleValues = {
        '--age-overlay-color': this.dataset.overlayColor,
        '--age-modal-background': this.dataset.modalBackground,
        '--age-text-color': this.dataset.textColor,
        '--age-muted-color': this.dataset.mutedColor,
        '--age-primary-background': this.dataset.primaryBackground,
        '--age-primary-text': this.dataset.primaryText,
        '--age-primary-hover': this.dataset.primaryHover,
        '--age-secondary-border': this.dataset.secondaryBorder,
        '--age-secondary-text': this.dataset.secondaryText,
        '--age-background-position': this.dataset.backgroundPosition,
        '--age-text-alignment': this.dataset.textAlignment
      };

      Object.entries(styleValues).forEach(([property, value]) => {
        if (value) {
          this.style.setProperty(property, value);
        }
      });

      const pixelValues = {
        '--age-backdrop-blur': this.dataset.backdropBlur,
        '--age-modal-width': this.dataset.modalWidth,
        '--age-modal-radius': this.dataset.modalRadius,
        '--age-button-radius': this.dataset.buttonRadius,
        '--age-logo-width': this.dataset.logoWidth,
        '--age-padding-desktop': this.dataset.paddingDesktop,
        '--age-padding-mobile': this.dataset.paddingMobile,
        '--age-heading-size-desktop':
          this.dataset.headingSizeDesktop,
        '--age-heading-size-mobile':
          this.dataset.headingSizeMobile,
        '--age-body-size-desktop': this.dataset.bodySizeDesktop,
        '--age-body-size-mobile': this.dataset.bodySizeMobile
      };

      Object.entries(pixelValues).forEach(([property, value]) => {
        const numericValue = Number.parseFloat(value);

        if (Number.isFinite(numericValue)) {
          this.style.setProperty(property, `${numericValue}px`);
        }
      });

      const overlayOpacity = this.clampNumber(
        Number.parseFloat(this.dataset.overlayOpacity),
        0,
        100,
        55
      );

      this.style.setProperty(
        '--age-overlay-opacity',
        String(overlayOpacity / 100)
      );
    }

    open() {
      if (!this.enabled) {
        return;
      }

      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      this.showDefaultState();

      this.previousActiveElement = document.activeElement;

      this.hidden = false;
      this.classList.remove('is-closing');
      this.setAttribute('aria-hidden', 'false');

      this.lockPage();

      document.removeEventListener('keydown', this.handleKeydown);
      document.addEventListener('keydown', this.handleKeydown);

      window.requestAnimationFrame(() => {
        if (this.confirmButton) {
          this.confirmButton.focus();
          return;
        }

        this.dialog?.focus();
      });
    }

    close({ animate = true, restoreFocus = true } = {}) {
      document.removeEventListener('keydown', this.handleKeydown);

      if (!animate) {
        this.hideImmediately({ restoreFocus });
        return;
      }

      this.classList.add('is-closing');

      this.closeTimer = window.setTimeout(() => {
        this.hideImmediately({ restoreFocus });
      }, 220);
    }

    hideImmediately({ restoreFocus = false } = {}) {
      this.hidden = true;
      this.classList.remove('is-closing');
      this.setAttribute('aria-hidden', 'true');

      document.removeEventListener('keydown', this.handleKeydown);

      this.unlockPage();

      if (
        restoreFocus &&
        this.previousActiveElement instanceof HTMLElement &&
        this.previousActiveElement.isConnected
      ) {
        this.previousActiveElement.focus();
      }
    }

    handleConfirm() {
      if (!this.isDesignMode) {
        this.saveConfirmation();
      }

      this.close();
    }

    handleDecline() {
      if (!this.content || !this.deniedPanel) {
        return;
      }

      this.content.hidden = true;
      this.deniedPanel.hidden = false;

      this.updateDialogReferences(
        this.deniedLabelledBy,
        this.deniedDescribedBy
      );

      window.requestAnimationFrame(() => {
        this.deniedHeading?.focus();
      });
    }

    showDefaultState() {
      if (this.content) {
        this.content.hidden = false;
      }

      if (this.deniedPanel) {
        this.deniedPanel.hidden = true;
      }

      this.updateDialogReferences(
        this.defaultLabelledBy,
        this.defaultDescribedBy
      );
    }

    updateDialogReferences(labelledBy, describedBy) {
      if (!this.dialog) {
        return;
      }

      if (labelledBy) {
        this.dialog.setAttribute('aria-labelledby', labelledBy);
      } else {
        this.dialog.removeAttribute('aria-labelledby');
      }

      if (describedBy) {
        this.dialog.setAttribute('aria-describedby', describedBy);
      } else {
        this.dialog.removeAttribute('aria-describedby');
      }
    }

    handleEditorClose() {
      if (!this.isDesignMode) {
        return;
      }

      this.close();
    }

    handleSectionSelect(event) {
      if (!this.isDesignMode) {
        return;
      }

      if (event.detail?.sectionId !== this.dataset.sectionId) {
        return;
      }

      this.open();
    }

    handleKeydown(event) {
      if (this.hidden) {
        return;
      }

      if (event.key === 'Escape' && this.isDesignMode) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = this.getFocusableElements();

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement =
        focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    getFocusableElements() {
      const selectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ];

      return Array.from(
        this.querySelectorAll(selectors.join(','))
      ).filter((element) => {
        return (
          !element.closest('[hidden]') &&
          element.getAttribute('aria-hidden') !== 'true'
        );
      });
    }

    hasValidConfirmation() {
      const storage = this.getStorage();

      if (!storage) {
        return false;
      }

      try {
        const savedValue = storage.getItem(this.storageKey);

        if (!savedValue) {
          return false;
        }

        if (this.storageMode === 'session') {
          return savedValue === 'accepted';
        }

        const confirmation = JSON.parse(savedValue);

        if (
          !confirmation ||
          typeof confirmation.expiresAt !== 'number'
        ) {
          storage.removeItem(this.storageKey);
          return false;
        }

        if (Date.now() >= confirmation.expiresAt) {
          storage.removeItem(this.storageKey);
          return false;
        }

        return true;
      } catch (error) {
        return false;
      }
    }

    saveConfirmation() {
      const storage = this.getStorage();

      if (!storage) {
        return;
      }

      try {
        if (this.storageMode === 'session') {
          storage.setItem(this.storageKey, 'accepted');
          return;
        }

        const millisecondsPerDay = 24 * 60 * 60 * 1000;
        const acceptedAt = Date.now();

        storage.setItem(
          this.storageKey,
          JSON.stringify({
            acceptedAt,
            expiresAt:
              acceptedAt +
              this.rememberDays * millisecondsPerDay
          })
        );
      } catch (error) {
        // Storage may be unavailable in strict privacy modes.
      }
    }

    getStorage() {
      try {
        return this.storageMode === 'session'
          ? window.sessionStorage
          : window.localStorage;
      } catch (error) {
        return null;
      }
    }

    lockPage() {
      if (!this.lockScroll) {
        return;
      }

      document.documentElement.classList.add('age-gate-open');
    }

    unlockPage() {
      document.documentElement.classList.remove('age-gate-open');
    }

    clampNumber(value, minimum, maximum, fallback) {
      if (!Number.isFinite(value)) {
        return fallback;
      }

      return Math.min(Math.max(value, minimum), maximum);
    }
  }

  customElements.define(ELEMENT_NAME, AgeVerification);
})();
